<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useGlobalUser } from "@/stores/globalUser";
import { useReply } from "@/stores/reply";
import { Moment, Reply } from "@/types/entity";

export interface ReplyTree extends Reply {
  replyTo?: Reply; // parentReply
  children: ReplyTree[];
}

const props = withDefaults(
  defineProps<{
    rootId?: number;
    entityId?: number;
    entityType?: "App\\Models\\StoryBook" | "App\\Models\\Collection";
    moment?: Moment;
    forceReFetch?: boolean;
  }>(),
  {
    forceReFetch: true,
  }
);
const emit = defineEmits<{
  (e: "onReplyClick", reply: Reply): void;
  (e: "onReactionClick", reply: Reply): void;
}>();

// State
const globalUser = useGlobalUser();
const replyStore = useReply();
const isReplyLoading = ref(false);
const replyList = computed(() => {
  if (props.entityId && props.entityType)
    return replyStore.getReplyTreeList(props.entityId);
  if (!props.rootId || !replyStore.getReplyList(props.rootId)) return [];
  return replyStore.getReplyList(props.rootId);
});
const replyTree = computed<ReplyTree[]>(() => {
  const tree: ReplyTree[] = [];
  const map = new Map<number, ReplyTree>();
  // 将replyList转换为map
  replyList.value?.forEach((item) => {
    map.set(item.id!, { ...item, children: [] });
  });
  replyList.value?.forEach((item) => {
    const parent = map.get(item.parentId);
    const current = map.get(item.id!)!;
    if (parent) {
      current.replyTo = parent;
      parent.children.push(current);
    } else if (item.mate) {
      tree.push(current);
    }
  });
  // 将children打平为数组
  const flatten = (tree: ReplyTree[]): ReplyTree[] => {
    return tree.reduce((acc, cur) => {
      if (cur.children) {
        acc.push(cur, ...flatten(cur.children));
      } else {
        acc.push(cur);
      }
      return acc;
    }, [] as ReplyTree[]);
  };
  tree.forEach((item) => {
    item.children = flatten(item.children!);
  });

  return tree;
});

watch(
  () => props.rootId,
  async (rootId) => {
    if (!rootId) return;
    try {
      isReplyLoading.value = true;
      await replyStore.getOrFetchReplyList(rootId, props.forceReFetch);
    } finally {
      isReplyLoading.value = false;
    }
  },
  { immediate: true }
);
watch(
  () => props.entityId,
  async (entityId) => {
    if (!entityId || !props.entityType) return;
    try {
      isReplyLoading.value = true;
      await replyStore.getOrFetchReplyTreeList(
        entityId,
        props.entityType,
        true
      );
    } finally {
      isReplyLoading.value = false;
    }
  },
  { immediate: true }
);
</script>
<template>
  <div>
    <div
      v-if="isReplyLoading || !rootId"
      class="flex items-center justify-center flex-grow"
    >
      <span class="loading loading-dots"></span>
    </div>
    <div v-else-if="replyTree.length === 0">
      <empty-content
        :desc="$t('Reply.noCommentsDesc')"
        :title="$t('Reply.noComments')"
      />
    </div>
    <div v-else class="flex flex-col gap-2">
      <div v-for="reply in replyTree" :key="reply.id">
        <TreeholeTreadReplyItem
          :id="'reply-' + reply.id"
          :avatar-url="reply.mate.mediaUrl"
          :content="
            reply.dtBlock ? $t(`Message.blockReplyContent`) : reply.content
          "
          :enable-reaction-btn="globalUser.isLogin"
          :enable-text-collapsed="false"
          :entityType="entityType"
          :mate-id="reply.mate.uuid"
          :media-list="reply.mediazz"
          :mentionMatezz="reply.mentionMatezz"
          :moment="moment"
          :reply="reply"
          :show-comment="reply.children.length === 0"
          :time="reply.dtCreate"
          :title="reply.mate.name"
          :type="reply.mate.type"
          :user="reply.mate.user"
          @comment-click="emit('onReplyClick', reply)"
          @reaction-click="emit('onReactionClick', reply)"
          :is-treehole="true"
        />
        <div v-if="reply.children && reply.children.length > 0">
          <TreeholeTreadReplyItem
            v-for="(subReply, idx) in reply.children"
            :key="subReply.id"
            :id="'reply-' + subReply.id"
            :avatar-url="subReply.mate.mediaUrl"
            :reply-length="reply.children.length"
            :content="
              subReply.dtBlock
                ? $t(`Message.blockReplyContent`)
                : subReply.content
            "
            :enable-reaction-btn="globalUser.isLogin"
            :enable-text-collapsed="false"
            :entityType="entityType"
            :mate-id="subReply.mate.uuid"
            :media-list="subReply.mediazz"
            :mentionMatezz="subReply.mentionMatezz"
            :moment="moment"
            :reply="subReply"
            :reply-to="subReply.replyTo"
            :show-comment="idx === reply.children.length - 1"
            :time="subReply.dtCreate"
            :title="subReply.mate.name"
            :type="subReply.mate.type"
            :user="subReply.user"
            class="pl-8"
            is-sub-reply
            @comment-click="emit('onReplyClick', subReply)"
            @reaction-click="emit('onReactionClick', subReply)"
            :is-treehole="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>
